import { DeleteOutlined } from '@ant-design/icons';
import {
    Button,
    Checkbox,
    Form,
    type FormInstance,
    type FormListFieldData,
    Input,
    Popconfirm,
    Tag,
    Tooltip,
} from 'antd';
import type { ProdutoLocalEstoque } from '@/features/produto-local-estoque/types/ProdutoLocalEstoque';
import type { FormProdutoValues } from '@/features/produto/types/FormProdutoValues';
import type { ProdutoBodyEstoque } from '@/features/produto/types/ProdutoBody';
import { TableDefault } from '@/components/antd/TableDefault';
import { InputNumber } from '@/components/input/InputNumber';
import { ProdutoLocalEstoqueUso } from '@/features/produto-local-estoque/components/ProdutoLocalEstoqueUso';
import { ProdutoLocalEstoqueStatus } from '@/features/produto-local-estoque/types/enum';
import { numberToDecimal } from '@/std/format';

type Row = {
    fieldKey: number;
    fieldName: number;
    local?: ProdutoLocalEstoque;
    produtoEstoque?: ProdutoBodyEstoque;
};

export function ProdutoTabEstoqueLocalTable({
    exibirAcoes,
    fields,
    form,
    locais,
    onDelete,
    produtoEstoque,
    setFormDirty,
    unidade,
}: {
    exibirAcoes: boolean;
    fields: FormListFieldData[];
    form: FormInstance<FormProdutoValues>;
    locais: ProdutoLocalEstoque[];
    onDelete: (index: number) => void;
    produtoEstoque: ProdutoBodyEstoque[];
    setFormDirty: (dirty: boolean) => void;
    unidade: string;
}) {
    const controlarEstoque =
        Form.useWatch('pro_controlar_estoque', { form, preserve: true }) ??
        form.getFieldValue('pro_controlar_estoque');

    const controlaEstoque = controlarEstoque === 1;

    const locaisById = new Map(locais.map((local) => [local.est_idpk, local]));

    const rows: Row[] = fields.map((field) => {
        const estoque =
            produtoEstoque[field.name] ?? form.getFieldValue(['produto_estoque', field.name]);

        return {
            fieldKey: field.key,
            fieldName: field.name,
            local: locaisById.get(estoque?.pre_estoque_idpk),
            produtoEstoque: estoque,
        };
    });

    return (
        <TableDefault<Row>
            columns={[
                {
                    align: 'center',
                    render: (_, row) => (
                        <>
                            <Form.Item hidden={true} name={[row.fieldName, 'pre_padrao']} />

                            <Tooltip
                                title={
                                    row.local?.est_permite_venda !== 'S'
                                        ? 'Este local de estoque não é usado para venda e não pode ser marcado como padrão.'
                                        : 'Local padrão para movimentações deste produto'
                                }
                            >
                                <Checkbox
                                    checked={row.produtoEstoque?.pre_padrao === 'S'}
                                    disabled={
                                        !controlaEstoque ||
                                        row.local?.est_status ===
                                            ProdutoLocalEstoqueStatus.Desativado ||
                                        row.local?.est_permite_venda !== 'S'
                                    }
                                    onChange={(event) => {
                                        const values: ProdutoBodyEstoque[] =
                                            form.getFieldValue('produto_estoque') ?? [];
                                        const checked = event.target.checked;

                                        form.setFieldValue(
                                            'produto_estoque',
                                            values.map((item, index) => {
                                                if (index === row.fieldName) {
                                                    return {
                                                        ...item,
                                                        pre_padrao: checked ? 'S' : 'N',
                                                    };
                                                }

                                                return checked
                                                    ? { ...item, pre_padrao: 'N' }
                                                    : item;
                                            }),
                                        );
                                        setFormDirty(true);
                                    }}
                                />
                            </Tooltip>
                        </>
                    ),
                    title: 'Padrão',
                    width: '5.5rem',
                },
                {
                    render: (_, row) => (
                        <>
                            <Form.Item hidden={true} name={[row.fieldName, 'pre_estoque_idpk']} />

                            <Form.Item hidden={true} name={[row.fieldName, 'pre_idpk']} />

                            {row.local?.est_descricao ??
                                row.produtoEstoque?.estoque?.est_descricao ??
                                `Local ${row.produtoEstoque?.pre_estoque_idpk ?? '-'}`}

                            {row.local?.est_status === ProdutoLocalEstoqueStatus.Desativado && (
                                <Tag className='ml-2' color='red'>
                                    Desativado
                                </Tag>
                            )}
                        </>
                    ),
                    title: 'Descrição',
                },
                {
                    render: (_, row) => <ProdutoLocalEstoqueUso local={row.local} />,
                    responsive: ['xl'],
                    title: 'Usado para',
                    width: '24rem',
                },
                {
                    render: (_, row) => (
                        <Form.Item className='mb-0' name={[row.fieldName, 'pre_localizacao']}>
                            <Input
                                aria-label={`Localização em ${row.local?.est_descricao ?? 'estoque'}`}
                                disabled={!controlaEstoque}
                                maxLength={100}
                                placeholder='Informe a localização'
                            />
                        </Form.Item>
                    ),
                    title: 'Localização',
                    width: '18rem',
                },
                {
                    align: 'right',
                    render: (_, row) => {
                        return (
                            <div className='ml-auto max-w-44'>
                                <Form.Item
                                    className='mb-0'
                                    name={[row.fieldName, 'pre_estoque_atual']}
                                    rules={[
                                        {
                                            message: 'Informe o total em estoque',
                                            required: true,
                                        },
                                    ]}
                                >
                                    <InputNumber
                                        addonAfter={
                                            unidade ? (
                                                <span className='flex items-center pl-2'>
                                                    {unidade}
                                                </span>
                                            ) : undefined
                                        }
                                        allowNegative={true}
                                        decimalScale={4}
                                        disabled={
                                            !controlaEstoque ||
                                            row.local?.est_permite_movimentacao_manual !== 'S'
                                        }
                                        onChange={(value) => {
                                            const values: ProdutoBodyEstoque[] =
                                                form.getFieldValue('produto_estoque') ?? [];

                                            form.setFieldValue(
                                                'estoque_total',
                                                values.reduce(
                                                    (total, item) =>
                                                        total +
                                                        (item === values[row.fieldName]
                                                            ? (value ?? 0)
                                                            : item.excluir === 'S'
                                                              ? 0
                                                              : (item.pre_estoque_atual ?? 0)),
                                                    0,
                                                ),
                                            );
                                        }}
                                    />
                                </Form.Item>

                                <Form.Item
                                    hidden={true}
                                    name={[row.fieldName, 'pre_estoque_inicial']}
                                />
                            </div>
                        );
                    },
                    title: 'Quantidade de estoque',
                    width: '15rem',
                },
                {
                    align: 'right',
                    render: (_, row) => (
                        <>
                            <Form.Item
                                hidden={true}
                                name={[row.fieldName, 'pre_estoque_reservado']}
                            />
                            {numberToDecimal(row.produtoEstoque?.pre_estoque_reservado, 4)}{' '}
                            {unidade}
                        </>
                    ),
                    responsive: ['xl'],
                    title: 'Reservado',
                    width: '9rem',
                },
                {
                    align: 'right',
                    render: (_, row) => {
                        const atual = row.produtoEstoque?.pre_estoque_atual ?? 0;
                        const reservado = row.produtoEstoque?.pre_estoque_reservado ?? 0;
                        const disponivel = numberToDecimal(atual - reservado, 4);

                        return (
                            <>
                                <Form.Item
                                    hidden={true}
                                    name={[row.fieldName, 'pre_estoque_disponivel']}
                                />
                                {disponivel} {unidade}
                            </>
                        );
                    },
                    responsive: ['md'],
                    title: 'Disponível',
                    width: '9rem',
                },
                {
                    align: 'center',
                    hidden: !exibirAcoes,
                    key: 'acoes',
                    render: (_, row) =>
                        row.local && row.local.est_produto_adicionar_automaticamente !== 'S' ? (
                            <Popconfirm
                                cancelText='Cancelar'
                                okButtonProps={{ danger: true }}
                                okText='Excluir'
                                onConfirm={() => onDelete(row.fieldName)}
                                title={`Deseja excluir o local "${row.local.est_descricao}" deste produto?`}
                            >
                                <Button
                                    aria-label={`Excluir ${row.local.est_descricao}`}
                                    danger={true}
                                    icon={<DeleteOutlined />}
                                    title='Excluir local de estoque'
                                    type='text'
                                />
                            </Popconfirm>
                        ) : null,
                    title: 'Ações',
                },
            ]}
            dataSource={rows}
            pagination={false}
            rowKey='fieldKey'
        />
    );
}
